import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { patientAdminApi } from "@/lib/patient-admin-api";
import type {
  ConditionInput,
  PatientAdminHealthUpdate,
  PatientAdminProfileIntake,
} from "@/types";

const patientAdminKeys = {
  overview: (patientId: string) =>
    ["patient-admin", "overview", patientId] as const,
  intake: (patientId: string) =>
    ["patient-admin", "intake", patientId] as const,
  diseaseSearch: (query: string) =>
    ["patient-admin", "disease-search", query] as const,
};

export const usePatientOverview = (patientId: string) =>
  useQuery({
    queryKey: patientAdminKeys.overview(patientId),
    queryFn: () => patientAdminApi.getOverview(patientId),
    enabled: !!patientId,
  });

export const usePatientProfileIntake = (patientId: string) =>
  useQuery({
    queryKey: patientAdminKeys.intake(patientId),
    queryFn: () => patientAdminApi.getProfileIntake(patientId),
    enabled: !!patientId,
  });

export const useDiseaseSearch = (query: string) =>
  useQuery({
    queryKey: patientAdminKeys.diseaseSearch(query),
    queryFn: () => patientAdminApi.searchDiseases(query),
    enabled: query.trim().length >= 2,
  });

export const usePatientProfileIntakeMutations = (patientId: string) => {
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: patientAdminKeys.intake(patientId),
      }),
      queryClient.invalidateQueries({
        queryKey: patientAdminKeys.overview(patientId),
      }),
    ]);
  };

  return {
    updateHealth: useMutation({
      mutationFn: (payload: PatientAdminHealthUpdate) =>
        patientAdminApi.updateHealth(patientId, payload),
      onSuccess: invalidate,
    }),
    replaceDiseases: useMutation({
      mutationFn: (items: ConditionInput[]) =>
        patientAdminApi.replaceDiseases(patientId, items),
      onSuccess: invalidate,
    }),
    replaceFamilyHistory: useMutation({
      mutationFn: (payload: {
        father: ConditionInput[];
        mother: ConditionInput[];
        sibling: ConditionInput[];
      }) => patientAdminApi.replaceFamilyHistory(patientId, payload),
      onSuccess: invalidate,
    }),
    replaceLifestyle: useMutation({
      mutationFn: (payload: PatientAdminProfileIntake["lifestyle"]) =>
        patientAdminApi.replaceLifestyle(patientId, payload),
      onSuccess: invalidate,
    }),
    markComplete: useMutation({
      mutationFn: () => patientAdminApi.markComplete(patientId),
      onSuccess: invalidate,
    }),
  };
};
